// AppointmentsPage.jsx
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AppointmentCalendar from "../components/appointments/AppointmentCalendar";
import useApiRequest from "../hooks/useApiRequest";
import useShellContext from "../layout/useShellContext";
import {
  getAppointments,
  updateAppointmentStatus,
  rescheduleAppointment,
  getPublicSlots,
} from "../services/appointmentsApi";
import {
  formatSlotTime,
  formatNow,
  parseAppointmentDateTime,
  shiftIsoDate,
} from "../utils/formatters";
import { appointmentStatusColors } from "../utils/helpers";
import { normalizeApiError } from "../utils/errors";

// Icon Components (updated to accept className prop)
const CalendarIcon = ({ className = "icon" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
    <line x1="16" y1="2" x2="16" y2="6"></line>
    <line x1="8" y1="2" x2="8" y2="6"></line>
    <line x1="3" y1="10" x2="21" y2="10"></line>
    <circle cx="12" cy="15" r="1"></circle>
    <circle cx="16" cy="15" r="1"></circle>
    <circle cx="8" cy="15" r="1"></circle>
  </svg>
);

const UserIcon = ({ className = "icon" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
    <circle cx="12" cy="7" r="4"></circle>
  </svg>
);

const PhoneIcon = ({ className = "icon" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.362 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.338 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"></path>
  </svg>
);

const MailIcon = ({ className = "icon" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
    <polyline points="22,6 12,13 2,6"></polyline>
  </svg>
);

const ClockIcon = ({ className = "icon" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"></circle>
    <polyline points="12 6 12 12 16 14"></polyline>
  </svg>
);

const StethoscopeIcon = ({ className = "icon" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4.8 2.3A.3.3 0 1 0 5 2.8a.3.3 0 0 0-.2-.5z"></path>
    <path d="M7 6h-2a2 2 0 0 0-2 2v6a6 6 0 0 0 12 0v-6a2 2 0 0 0-2-2h-2"></path>
    <path d="M8 6v10a4 4 0 0 0 8 0v-6a2 2 0 0 0-2-2h-2"></path>
    <circle cx="17" cy="15" r="3"></circle>
  </svg>
);

const NoteIcon = ({ className = "icon" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
    <polyline points="14 2 14 8 20 8"></polyline>
    <line x1="16" y1="13" x2="8" y2="13"></line>
    <line x1="16" y1="17" x2="8" y2="17"></line>
    <polyline points="10 9 9 9 8 9"></polyline>
  </svg>
);

const DoctorIcon = ({ className = "icon" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="9" y="2" width="6" height="16" rx="1"></rect>
    <path d="M14 7h5a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-5"></path>
    <path d="M10 7H5a1 1 0 0 0-1 1v3a1 1 0 0 0 1 1h5"></path>
    <path d="M8 18h8"></path>
    <path d="M12 18v3"></path>
  </svg>
);

const RescheduleIcon = ({ className = "icon" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
    <circle cx="12" cy="12" r="3"></circle>
  </svg>
);

const CancelIcon = ({ className = "icon" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const FilterIcon = ({ className = "icon" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polygon points="22 3 2 3 10 13 10 21 14 18 14 13 22 3"></polygon>
  </svg>
);

const SearchIcon = ({ className = "icon" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8"></circle>
    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
  </svg>
);

const ChevronRightIcon = ({ className = "icon small" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="9 18 15 12 9 6"></polyline>
  </svg>
);

const CloseIcon = ({ className = "icon" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

// Get current month's date range
function getCurrentMonthRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  
  return {
    from: start.toISOString().slice(0, 10),
    to: end.toISOString().slice(0, 10),
  };
}

export default function AppointmentsPage() {
  const { showToast, registerPageControls, resetPageControls } = useShellContext();
  const { data, error, run } = useApiRequest({
    appointments: [],
  });
  const [statusFilter, setStatusFilter] = useState("all");
  const [serviceFilter, setServiceFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [visibleRange, setVisibleRange] = useState(getCurrentMonthRange());
  const [appointmentBusy, setAppointmentBusy] = useState(false);
  const [showRescheduleForm, setShowRescheduleForm] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [rescheduleData, setRescheduleData] = useState({
    date: "",
    slot: "",
    slots: [],
    loading: false
  });
  const isFirstLoad = useRef(true);
  const lastRequestRef = useRef({ statusFilter, visibleRange });
  const mountedRef = useRef(false);
  const [lastUpdated, setLastUpdated] = useState("--");
  const [shellRefreshing, setShellRefreshing] = useState(false);
  const visibleFrom = visibleRange?.from || "";
  const visibleTo = visibleRange?.to || "";

  // Extract unique services from appointments
  const availableServices = useMemo(() => {
    const appointments = data?.appointments || [];
    const services = new Set();
    services.add("all");
    appointments.forEach(app => {
      if (app.service_type) {
        services.add(app.service_type.toLowerCase());
      }
      if (app.service_name) {
        services.add(app.service_name.toLowerCase());
      }
    });
    return Array.from(services);
  }, [data?.appointments]);

  // Filter appointments based on service
  const filteredByService = useMemo(() => {
    const appointments = data?.appointments || [];
    if (serviceFilter === "all") return appointments;
    return appointments.filter(app => 
      app.service_type?.toLowerCase() === serviceFilter ||
      app.service_name?.toLowerCase() === serviceFilter
    );
  }, [data?.appointments, serviceFilter]);

  // Filter appointments based on search query
  const filteredBySearch = useMemo(() => {
    if (!searchQuery.trim()) return filteredByService;
    
    const query = searchQuery.toLowerCase().trim();
    return filteredByService.filter(app => 
      app.patient_name?.toLowerCase().includes(query) ||
      app.title?.toLowerCase().includes(query) ||
      app.patient_id?.toLowerCase().includes(query) ||
      app.id?.toString().includes(query) ||
      app.phone?.toLowerCase().includes(query) ||
      app.email?.toLowerCase().includes(query) ||
      app.service_type?.toLowerCase().includes(query) ||
      app.service_name?.toLowerCase().includes(query)
    );
  }, [filteredByService, searchQuery]);

  // Group appointments by date for the left sidebar
  const groupedAppointments = useMemo(() => {
    const grouped = {};
    filteredBySearch.forEach(app => {
      const date = app.date;
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(app);
    });
    // Sort dates
    return Object.keys(grouped).sort().reduce((obj, key) => {
      obj[key] = grouped[key];
      return obj;
    }, {});
  }, [filteredBySearch]);

  const loadAppointments = useCallback(
    async ({ signal } = {}) => {
      if (!visibleFrom || !visibleTo) {
        return { appointments: [] };
      }

      const response = await getAppointments(
        {
          from: visibleFrom,
          to: visibleTo,
          status: statusFilter === "all" ? undefined : statusFilter,
        },
        { signal },
      );

      return {
        appointments: Array.isArray(response)
          ? response
          : response?.appointments || [],
      };
    },
    [statusFilter, visibleFrom, visibleTo],
  );

  const refreshAppointments = useCallback(
    async ({ trackRefresh = false } = {}) => {
      if (trackRefresh && mountedRef.current) {
        setShellRefreshing(true);
      }

      try {
        const result = await run(loadAppointments);

        if (mountedRef.current) {
          setLastUpdated(formatNow());
        }

        return result;
      } catch (err) {
        if (err?.name !== "AbortError" && process.env.NODE_ENV === "development") {
          console.error("Refresh failed:", err);
        }
        return undefined;
      } finally {
        if (trackRefresh && mountedRef.current) {
          setShellRefreshing(false);
        }
      }
    },
    [loadAppointments, run],
  );

  const handleRefresh = useCallback(
    () => refreshAppointments({ trackRefresh: true }),
    [refreshAppointments],
  );

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const hasChanged = 
      lastRequestRef.current.statusFilter !== statusFilter ||
      lastRequestRef.current.visibleRange?.from !== visibleFrom ||
      lastRequestRef.current.visibleRange?.to !== visibleTo;
    
    if (!hasChanged && !isFirstLoad.current) {
      return;
    }
    
    lastRequestRef.current = {
      statusFilter,
      visibleRange: {
        from: visibleFrom,
        to: visibleTo,
      },
    };
    
    if (isFirstLoad.current) {
      isFirstLoad.current = false;
    }
    
    refreshAppointments();
    
  }, [statusFilter, visibleFrom, visibleTo, refreshAppointments]);

  useEffect(() => {
    registerPageControls({
      onRefresh: handleRefresh,
      refreshEvery: "Manual",
      lastUpdated,
      isRefreshing: shellRefreshing,
    });

    return () => {
      resetPageControls();
    };
  }, [handleRefresh, lastUpdated, registerPageControls, resetPageControls, shellRefreshing]);

  const events = useMemo(
    () =>
      (data?.appointments || []).map((item) => {
        const startIso = parseAppointmentDateTime(item.date, item.time);
        const startDate = startIso
          ? new Date(startIso)
          : new Date(`${item.date}T12:00:00+04:00`);
        const endDate = new Date(startDate.getTime() + 30 * 60 * 1000);
        const palette = appointmentStatusColors(item.status);

        return {
          id: item.id,
          title: `${item.title || item.patient_name || "Appointment"}`,
          start: startDate,
          end: endDate,
          backgroundColor: palette.backgroundColor,
          borderColor: palette.borderColor,
          textColor: palette.textColor,
          extendedProps: { ...item },
        };
      }),
    [data?.appointments],
  );

  async function loadRescheduleSlots(date, serviceType) {
    if (!date) return;

    setRescheduleData(prev => ({ ...prev, loading: true, slots: [] }));

    try {
      const response = await getPublicSlots({
        date: String(date).slice(0, 10),
        service_type: String(serviceType || "clinic").toLowerCase(),
      });

      const slots = Array.isArray(response?.available_slots)
        ? response.available_slots
        : Array.isArray(response?.slots)
          ? response.slots
          : [];

      setRescheduleData(prev => ({
        ...prev,
        slots,
        loading: false
      }));
    } catch (error) {
      setRescheduleData(prev => ({ ...prev, slots: [], loading: false }));
      showToast("Failed to load available slots", true, 4000);
    }
  }

  async function handleStatusUpdate(nextStatus) {
    if (!selectedAppointment?.id || appointmentBusy) return;

    setAppointmentBusy(true);

    try {
      await updateAppointmentStatus(selectedAppointment.id, nextStatus);
      await refreshAppointments();
      setSelectedAppointment(prev => ({ ...prev, status: nextStatus }));
      showToast(
        nextStatus === "booked"
          ? "Appointment confirmed successfully"
          : "Appointment cancelled successfully",
        false,
        4000,
      );
    } catch (error) {
      const normalizedError = normalizeApiError(error);
      if (!normalizedError.isAbort) {
        showToast(normalizedError.message, true, 6000);
      }
    } finally {
      setAppointmentBusy(false);
    }
  }

  async function handleReschedule() {
    if (!selectedAppointment?.id || !rescheduleData.date || !rescheduleData.slot || appointmentBusy) {
      showToast("Please select a date and time slot", true, 4000);
      return;
    }

    setAppointmentBusy(true);

    try {
      await rescheduleAppointment(selectedAppointment.id, {
        new_date: rescheduleData.date,
        new_time_slot: rescheduleData.slot,
        service_type: selectedAppointment.service_type || "clinic",
      });
      await refreshAppointments();
      setShowRescheduleForm(false);
      setRescheduleData({ date: "", slot: "", slots: [], loading: false });
      showToast("Appointment rescheduled successfully", false, 5000);
    } catch (error) {
      const normalizedError = normalizeApiError(error);
      if (!normalizedError.isAbort) {
        showToast(normalizedError.message, true, 6000);
      }
    } finally {
      setAppointmentBusy(false);
    }
  }

  const handleSelectAppointment = (appointment) => {
    setSelectedAppointment(appointment);
    setShowRescheduleForm(false);
    setRescheduleData({ date: "", slot: "", slots: [], loading: false });
    setIsSidebarOpen(true);
  };

  const handleCloseSidebar = () => {
    setIsSidebarOpen(false);
    setTimeout(() => {
      setSelectedAppointment(null);
      setShowRescheduleForm(false);
    }, 300);
  };

  return (
    <div className="appointments-page">
      {/* LEFT SIDEBAR */}
      <div className="sidebar-left">
        <div className="sidebar-header">
          <h2>
            <CalendarIcon />
            Appointments
          </h2>
          
          <div className="appointments-search-group">
            <SearchIcon className="appointments-search-icon" />
            <input
              type="text"
              placeholder="Search by patient name, ID, phone, email, or service..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="appointments-search-input"
            />
            {searchQuery && (
              <button 
                className="appointments-clear-search-btn"
                onClick={() => setSearchQuery("")}
              >
                <CloseIcon className="appointments-clear-icon" />
              </button>
            )}
          </div>
          
          <div className="filter-group">
            <label>
              <FilterIcon />
              Service Type
            </label>
            <select
              value={serviceFilter}
              onChange={(e) => setServiceFilter(e.target.value)}
              className="filter-select"
            >
              {availableServices.map(service => (
                <option key={service} value={service}>
                  {service === "all" ? "All Services" : service.charAt(0).toUpperCase() + service.slice(1)}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Statuses</option>
              <option value="requested">Requested</option>
              <option value="booked">Confirmed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>

        <div className="appointments-list">
          {Object.entries(groupedAppointments).map(([date, appointments]) => (
            <div key={date} className="date-group">
              <div className="date-header">
                <span className="date">
                  {new Date(date).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                </span>
                <span className="count">{appointments.length}</span>
              </div>
              {appointments.map(appointment => (
                <div
                  key={appointment.id}
                  className={`appointment-card ${selectedAppointment?.id === appointment.id && isSidebarOpen ? 'active' : ''}`}
                  onClick={() => handleSelectAppointment(appointment)}
                >
                  <div className="appointment-time">{appointment.time}</div>
                  <div className="appointment-details">
                    <div className="patient-name">
                      {appointment.patient_name || appointment.title || "Patient"}
                    </div>
                    <div className="service-name">
                      <StethoscopeIcon />
                      {appointment.service_type || appointment.service_name || "General"}
                    </div>
                  </div>
                  <div className={`status-badge ${appointment.status}`}>
                    {appointment.status}
                  </div>
                  <ChevronRightIcon />
                </div>
              ))}
            </div>
          ))}
          {filteredBySearch.length === 0 && (
            <div className="empty-state">
              <CalendarIcon />
              <p>{searchQuery ? "No matching appointments found" : "No appointments found"}</p>
            </div>
          )}
        </div>
      </div>

      {/* CENTER - CALENDAR */}
      <div className="calendar-center">
        <div className="calendar-wrapper">
          <AppointmentCalendar
            events={events}
            refreshSignal={`${visibleRange?.from || ""}-${visibleRange?.to || ""}-${statusFilter}`}
            emptyTitle={error ? "Unable to load appointments" : "No appointments"}
            emptySubtitle={error ? "Please try refreshing" : "Select a date range to view appointments"}
            onDatesChange={(info) => {
              const nextRange = {
                from: String(info.startStr || "").slice(0, 10),
                to: shiftIsoDate(String(info.endStr || "").slice(0, 10), -1),
              };
              if (visibleRange?.from !== nextRange.from || visibleRange?.to !== nextRange.to) {
                setVisibleRange(nextRange);
              }
            }}
            onSelectAppointment={handleSelectAppointment}
          />
        </div>
      </div>

      {/* OVERLAY */}
      {isSidebarOpen && <div className="sidebar-overlay" onClick={handleCloseSidebar} />}

      {/* RIGHT SIDEBAR - SLIDE OUT */}
      <div className={`sidebar-right-slide ${isSidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-right-header">
          <button className="close-sidebar-btn" onClick={handleCloseSidebar}>
            <CloseIcon />
          </button>
        </div>

        {selectedAppointment ? (
          <>
            <div className="profile-card">
              <div className="profile-avatar">
                {selectedAppointment.patient_name?.[0] || selectedAppointment.title?.[0] || "P"}
              </div>
              <h3>{selectedAppointment.patient_name || selectedAppointment.title || "Patient"}</h3>
              <p className="patient-id">ID: {selectedAppointment.patient_id || selectedAppointment.id}</p>
            </div>

            {!showRescheduleForm ? (
              <>
                <div className="details-section">
                  <h4>
                    <UserIcon />
                    Contact Information
                  </h4>
                  <div className="detail-row">
                    <PhoneIcon />
                    <span className="label">Phone:</span>
                    <span className="value">{selectedAppointment.phone || "Not provided"}</span>
                  </div>
                  <div className="detail-row">
                    <MailIcon />
                    <span className="label">Email:</span>
                    <span className="value">{selectedAppointment.email || "Not provided"}</span>
                  </div>
                </div>

                <div className="details-section">
                  <h4>
                    <ClockIcon />
                    Appointment Details
                  </h4>
                  <div className="detail-row">
                    <span className="label">Date & Time:</span>
                    <span className="value">{selectedAppointment.date} at {selectedAppointment.time}</span>
                  </div>
                  <div className="detail-row">
                    <StethoscopeIcon />
                    <span className="label">Service:</span>
                    <span className="value">{selectedAppointment.service_type || selectedAppointment.service_name || "General"}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Status:</span>
                    <span className={`status-value ${selectedAppointment.status}`}>{selectedAppointment.status}</span>
                  </div>
                </div>

                {selectedAppointment.notes && (
                  <div className="details-section">
                    <h4>
                      <NoteIcon />
                      Notes
                    </h4>
                    <div className="notes-text">{selectedAppointment.notes}</div>
                  </div>
                )}

                {selectedAppointment.doctor_name && (
                  <div className="details-section">
                    <h4>
                      <DoctorIcon />
                      Doctor
                    </h4>
                    <div className="doctor-name">{selectedAppointment.doctor_name}</div>
                  </div>
                )}

                <div className="action-buttons">
                  <button 
                    className="btn-reschedule"
                    onClick={() => setShowRescheduleForm(true)}
                    disabled={appointmentBusy}
                  >
                    <RescheduleIcon />
                    Reschedule
                  </button>
                  {selectedAppointment.status !== "cancelled" && selectedAppointment.status !== "booked" && (
                    <button 
                      className="btn-confirm"
                      onClick={() => handleStatusUpdate("booked")}
                      disabled={appointmentBusy}
                    >
                      Confirm
                    </button>
                  )}
                  {selectedAppointment.status !== "cancelled" && (
                    <button 
                      className="btn-cancel"
                      onClick={() => handleStatusUpdate("cancelled")}
                      disabled={appointmentBusy}
                    >
                      <CancelIcon />
                      Cancel
                    </button>
                  )}
                </div>
              </>
            ) : (
              <div className="reschedule-form">
                <h4>Reschedule Appointment</h4>
                <div className="form-group">
                  <label>Select New Date</label>
                  <input
                    type="date"
                    value={rescheduleData.date}
                    min={new Date().toISOString().slice(0, 10)}
                    onChange={(e) => {
                      setRescheduleData({ ...rescheduleData, date: e.target.value, slot: "" });
                      loadRescheduleSlots(e.target.value, selectedAppointment.service_type);
                    }}
                  />
                </div>

                {rescheduleData.loading && (
                  <div className="loading-slots">Loading available slots...</div>
                )}

                {rescheduleData.slots.length > 0 && !rescheduleData.loading && (
                  <div className="form-group">
                    <label>Select Time Slot</label>
                    <div className="slots-grid">
                      {rescheduleData.slots.map(slot => (
                        <button
                          key={slot}
                          className={`slot-btn ${rescheduleData.slot === slot ? 'selected' : ''}`}
                          onClick={() => setRescheduleData({ ...rescheduleData, slot })}
                        >
                          {slot}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="reschedule-actions">
                  <button 
                    className="btn-back"
                    onClick={() => {
                      setShowRescheduleForm(false);
                      setRescheduleData({ date: "", slot: "", slots: [], loading: false });
                    }}
                  >
                    Back
                  </button>
                  <button 
                    className="btn-submit"
                    onClick={handleReschedule}
                    disabled={!rescheduleData.date || !rescheduleData.slot || appointmentBusy}
                  >
                    Confirm Reschedule
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="no-selection">
            <CalendarIcon />
            <p>Select an appointment to view details</p>
          </div>
        )}
      </div>
    </div>
  );
}